/**
 * The floor plan icon: a tiny walls-only rendering of a room. It draws the
 * resolved footprint outline (base rectangle plus alcoves, chamfers
 * included; doors, windows, and openings sit in the wall and don't alter the
 * footprint, so they're naturally ignored) plus any free-floating `wall`
 * elements as solid slabs.
 *
 * The geometry is normalized into a fixed-size icon viewBox with a fixed pixel
 * stroke width, so the outline reads at the same visual weight for every room
 * regardless of its real-world dimensions. See `FloorPlanIconSvgOptions`.
 */

import { freeElementType } from './freeElementTypes';
import { freeElementRing } from './geometry';
import { resolvePerimeter } from './resolve';
import { type FloorPlanRoom, Point, roomFreeElements } from './schemas';

export interface FloorPlanIconSvgOptions {
  /** Icon canvas size (both width and height), in pixels. Default 64. */
  size?: number;
  /** Margin between the room outline and the canvas edge, in pixels. Default 5. */
  padding?: number;
  /** Stroke thickness, in pixels, fixed regardless of room size. Default 1. */
  lineThickness?: number;
  /** Stroke (and free-wall fill) color. Default `black`. */
  lineColor?: string;
}

/**
 * Render a room as a normalized icon: the perimeter outline traced at a
 * fixed pixel line thickness inside a fixed-size square viewBox, regardless
 * of the room's real-world dimensions. The geometry is pre-scaled to fit the
 * icon canvas (aspect ratio preserved, centered, padded), so every room's
 * outline reads at the same visual weight side by side.
 */
export function floorPlanIconSvg(room: FloorPlanRoom, options: FloorPlanIconSvgOptions = {}): string {
  const size = options.size ?? 64;
  const padding = options.padding ?? 5;
  const lineThickness = options.lineThickness ?? 1;
  const lineColor = options.lineColor ?? 'black';

  const { polygons } = resolvePerimeter(room);
  const wallRings = roomFreeElements(room)
    .filter(e => freeElementType(e).floorRole === 'carves')
    .map(e => freeElementRing(e));

  const points = [...polygons.flat(2), ...wallRings.flat()];
  const xs = points.map(p => p.x);
  const ys = points.map(p => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const modelWidth = Math.max(Math.max(...xs) - minX, 0.001);
  const modelHeight = Math.max(Math.max(...ys) - minY, 0.001);

  const drawableSize = Math.max(size - 2 * padding, 0.001);
  const scale = drawableSize / Math.max(modelWidth, modelHeight);
  const offsetX = padding + (drawableSize - modelWidth * scale) / 2;
  const offsetY = padding + (drawableSize - modelHeight * scale) / 2;
  const toIcon = (p: Point): Point => ({
    x: offsetX + (p.x - minX) * scale,
    y: offsetY + (p.y - minY) * scale,
  });

  const footprint = polygons.map(poly => poly.map(ring => ringPath(ring.map(toIcon))).join(' ')).join(' ');
  const strokeAttrs = `stroke="${lineColor}" stroke-width="${fmt(lineThickness)}" stroke-linejoin="round" stroke-linecap="round"`;
  const parts = [`<path d="${footprint}" fill="none" fill-rule="evenodd" ${strokeAttrs}/>`];
  if (wallRings.length > 0) {
    const walls = wallRings.map(ring => ringPath(ring.map(toIcon))).join(' ');
    parts.push(`<path d="${walls}" fill="${lineColor}" ${strokeAttrs}/>`);
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${fmt(size)} ${fmt(size)}">${parts.join('')}</svg>`;
}

/** One closed ring as an SVG path command string. */
function ringPath(ring: Point[]): string {
  if (ring.length === 0) return '';
  const [first, ...rest] = ring;
  const cmds = [`M${fmt(first.x)} ${fmt(first.y)}`, ...rest.map(p => `L${fmt(p.x)} ${fmt(p.y)}`), 'Z'];
  return cmds.join(' ');
}

/** Compact coordinates: 2 decimals (~⅛ inch in feet) with trailing zeros trimmed. */
function fmt(n: number): string {
  return String(Math.round(n * 100) / 100);
}
